import { Card, Col, Form, Input, Row, Select } from "antd";
import HandleNotification from "common/Notification";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  GET_BUYER_ENDPOINT,
  GET_BUYING_OFFICE_ENDPOINT,
  NEW_TNA_TEMPLATE_BASE,
  NEW_TNA_TEMPLATE_UPDATE_DELETE_BASE,
} from "apiServices/API_ENDPOINTS";
import { getData, postData, putData } from "apiServices/common";
import { isArrayAndHasValue } from "utils/functions";
import ItemDetails from "../ItemDetails";

const AddOrUpdateForm = forwardRef((props, ref) => {
  const { view, isEdit } = props;

  const [buyingOfficeList, setBuyingOfficeList] = useState([]);
  const [buyerList, setBuyerList] = useState([]);
  const [itemDetailsList, setItemDetailsList] = useState([]);
  const [previousTemplateList, setPreviousTemplateList] = useState([]);

  const { templateID } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { Option } = Select;

  const getPreviousTemplateList = async (filterValues) => {
    const response = await getData(NEW_TNA_TEMPLATE_BASE, false, {
      per_page: 100,
      ...(filterValues?.title ? { title: filterValues?.title } : {}),
    });

    if (response?.data?.code === 200) {
      setPreviousTemplateList(response?.data?.data?.data || []);
    }
  };

  const mapTemplateTask = (item) => {
    return {
      id: item?.id,
      task_id: item?.task_id || item?.task?.id,
      task_name: item?.task?.title || item?.task_title,
      process_id: item?.process_id || item?.process?.id,
      process_name: item?.process?.title || item?.process_title,
      relation_with: item?.relation_with || item?.task?.relation_with || "",
      responsible_person_id: item?.responsible_person_id || item?.responsible_person?.id,
      responsible_person_name: item?.responsible_person?.name || "",
      days: item?.days,
      calculation_method: item?.calculation_method || "Backward from PCD",
      is_add: true,
    };
  };

  const getTemplateDetails = useCallback(
    async (id, options = {}) => {
      const { updateMasterFields = false } = options;
      const response = await getData(`${NEW_TNA_TEMPLATE_BASE}/${id}/show`);
      if (response?.data?.code === 200) {
        const data = response?.data?.data;

        if (updateMasterFields) {
          if (data?.buying_office?.id && !buyingOfficeList.some((office) => office?.id === data?.buying_office?.id)) {
            setBuyingOfficeList((prev) => [...prev, data?.buying_office]);
          }

          if (data?.buyer?.id && !buyerList.some((buyer) => buyer?.id === data?.buyer?.id)) {
            setBuyerList((prev) => [...prev, data?.buyer]);
          }

          form.setFieldsValue({
            title: data?.title,
            buyer_id: data?.buyer?.id || data?.buyer_id,
            buying_office_id: data?.buying_office?.id || data?.buying_office_id,
            copy_from_previous_template: data?.copy_from_previous_template,
          });
        }

        const taskRows = isArrayAndHasValue(data?.tna_details) ? data?.tna_details.map(mapTemplateTask) : [];
        setItemDetailsList(taskRows);
      }
    },
    [form, buyerList, buyingOfficeList],
  );

  useEffect(() => {
    if (isEdit && templateID) {
      getTemplateDetails(templateID, { updateMasterFields: true });
    }
  }, [isEdit, templateID, getTemplateDetails]);

  useImperativeHandle(ref, () => ({
    submit() {
      form
        .validateFields()
        .then(async (values) => {
          const bodyData = {
            title: values?.title,
            buyer_id: values?.buyer_id,
            buying_office_id: values?.buying_office_id,
            copy_from_previous_template: values?.copy_from_previous_template || "",
            template_tasks: itemDetailsList?.map((item) => ({
              ...(item?.id ? { id: item?.id } : {}),
              task_id: item?.task_id,
              process_id: item?.process_id,
              relation_with: item?.relation_with || "",
              calculation_method: "Backward from PCD",
              responsible_person_id: item?.responsible_person_id,
              days: Number(item?.days),
            })),
          };

          if (!isArrayAndHasValue(bodyData?.template_tasks)) {
            HandleNotification("error", "bottomRight", "Please add at least one task", null);
            return;
          }

          if (!isEdit) {
            const response = await postData(NEW_TNA_TEMPLATE_BASE, bodyData);
            if (response?.code === 200) {
              HandleNotification("success", "bottomRight", "New TNA template created successfully", null);
              const editID = response?.data?.id;
              if (editID) {
                navigate(`/tanda/update-new-template/${editID}`);
              }
              return;
            }

            HandleNotification("error", "bottomRight", "Error creating new TNA template", null);
            return;
          }

          const updateResponse = await putData(`${NEW_TNA_TEMPLATE_BASE}/${templateID}`, bodyData);

          if (updateResponse?.code === 200) {
            HandleNotification("success", "bottomRight", "New TNA template updated successfully", null);
            return;
          }

          HandleNotification("error", "bottomRight", "Error updating new TNA template", null);
        })
        .catch(() => {
          HandleNotification("error", "bottomRight", "Please check required fields", null);
        });
    },
    discard() {
      form.resetFields();
    },
  }));

  const getBuyingOfficeList = async (name) => {
    const response = await getData(GET_BUYING_OFFICE_ENDPOINT, false, {
      per_page: 100,
      ...(name ? { name } : {}),
    });

    if (response?.data?.code === 200) {
      setBuyingOfficeList(response?.data?.data?.data || []);
    }
  };

  const getBuyerList = async (name) => {
    const response = await getData(GET_BUYER_ENDPOINT, false, {
      per_page: 100,
      ...(name ? { name } : {}),
    });

    if (response?.data?.code === 200) {
      setBuyerList(response?.data?.data?.data || []);
    }
  };

  useEffect(() => {
    getBuyingOfficeList();
    getBuyerList();
    getPreviousTemplateList();
  }, []);

  return (
    <div className="containt-body">
      <Form layout="vertical" form={form} name="new-tna-template-form">
        <Card>
          <Row gutter={8}>
            <Col span={6}>
              <Form.Item
                label="Title"
                name="title"
                rules={[
                  {
                    required: true,
                    message: "Please input title",
                  },
                ]}
              >
                <Input size="small" placeholder="Title" disabled={view} />
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item
                label="Buyer"
                name="buyer_id"
                rules={[
                  {
                    required: true,
                    message: "Please select buyer",
                  },
                ]}
              >
                <Select
                  size="small"
                  showSearch
                  placeholder="Select buyer"
                  optionFilterProp="children"
                  disabled={view}
                  onSearch={getBuyerList}
                  allowClear
                >
                  {isArrayAndHasValue(buyerList) &&
                    buyerList.map((item) => (
                      <Option key={item?.id} value={item?.id}>
                        {item?.name}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item
                label="Buying Office"
                name="buying_office_id"
                rules={[
                  {
                    required: true,
                    message: "Please select buying office",
                  },
                ]}
              >
                <Select
                  size="small"
                  showSearch
                  placeholder="Select buying office"
                  optionFilterProp="children"
                  disabled={view}
                  onSearch={getBuyingOfficeList}
                  allowClear
                >
                  {isArrayAndHasValue(buyingOfficeList) &&
                    buyingOfficeList.map((item) => (
                      <Option key={item?.id} value={item?.id}>
                        {item?.name}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item name="copy_from_previous_template" label="Copy From Previous Template">
                <Select
                  size="small"
                  showSearch
                  allowClear
                  disabled={view}
                  placeholder="Copy from previous template"
                  optionFilterProp="children"
                  onSearch={(value) => getPreviousTemplateList({ title: value })}
                  onSelect={(value) => getTemplateDetails(value, { updateMasterFields: false })}
                >
                  {isArrayAndHasValue(previousTemplateList) &&
                    previousTemplateList.map((item) => (
                      <Option key={item?.id} value={item?.id}>
                        {item?.title}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <ItemDetails view={view} itemDetailsList={itemDetailsList} setItemDetailsList={setItemDetailsList} />
      </Form>
    </div>
  );
});

export default AddOrUpdateForm;
